import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext.jsx';
import DashboardCard from '../../components/DashboardCard.jsx';
import { Link } from 'react-router-dom';

// FIX: Change to the correct two-level depth path: ../../firebase/firebase.js
import { db } from '../../firebase/firebase.js';


// --- 1. Examiner Management Component --- (REMAINS UNCHANGED)
function ExaminerManagement() {
    const { manageExaminer } = useAuth();
    // ... (rest of the ExaminerManagement component remains unchanged)
    const [examinerUid, setExaminerUid] = useState('');
    const [subjects, setSubjects] = useState('');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage("");
        setLoading(true);
        const subjectsArray = subjects.split(',').map(s => s.trim()).filter(s => s.length > 0);
        try {
            await manageExaminer({
                uid: examinerUid.trim(),
                subjects: subjectsArray,
            });
            setMessage('Examiner profile saved.');
            setExaminerUid('');
            setSubjects('');
        } catch (err) {
            const errorMessage = err.message || 'Failed to update examiner profile.';
            setMessage(`Error: ${errorMessage}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <DashboardCard title="Manage Examiners & Subjects" className="w-full">
            <form onSubmit={handleSubmit} className="space-y-4">
                <p className="text-sm text-gray-500">Designate users as examiners and assign their subjects of expertise.</p>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Examiner User ID (UID)</label>
                    <input
                        type="text"
                        value={examinerUid}
                        onChange={(e) => setExaminerUid(e.target.value)}
                        required
                        className="mt-1 w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:focus:border-blue-500"
                        placeholder="Enter Examiner UID"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Subjects (Comma Separated)</label>
                    <input
                        type="text"
                        value={subjects}
                        onChange={(e) => setSubjects(e.target.value)}
                        required
                        className="mt-1 w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Math, Physics, English"
                    />
                </div>
                <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-blue-600 text-white p-2 rounded-md font-semibold hover:bg-blue-700 disabled:bg-gray-400"
                >
                    {loading ? 'Saving Profile...' : 'Save Examiner Profile'}
                </button>
            </form>
            {message && (
                <p className={`mt-4 text-sm font-semibold ${message.startsWith('Error') ? 'text-red-600' : 'text-green-600'}`}>
                    {message}
                </p>
            )}
        </DashboardCard>
    );
}

// --- 2. Assignment Control --- (REMAINS UNCHANGED)
function AssignmentControl() {
    const { assignAllPendingSubmissions } = useAuth();
    // ... (rest of the AssignmentControl component remains unchanged)
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');

    const handleAssign = async () => {
        setLoading(true);
        setMessage('');
        try {
            const result = await assignAllPendingSubmissions();
            setMessage(result.message);
        } catch (err) {
            setMessage(`Error: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <DashboardCard title="Assignment Controls" className="w-full">
            <h4 className="font-semibold text-lg mb-2">Automate Grading Distribution</h4>
            <p className="text-sm text-gray-600 mb-4">
                This process finds all submissions needing manual grading and assigns them to the qualified examiner with the lowest workload.
            </p>
            <button
                onClick={handleAssign}
                disabled={loading}
                className="w-full bg-indigo-600 text-white p-2 rounded-md font-semibold hover:bg-indigo-700 disabled:bg-gray-400"
            >
                {loading ? 'Processing...' : 'Assign Pending Submissions'}
            </button>
            {message && <p className={`mt-3 text-sm font-semibold ${message.startsWith('Error') ? 'text-red-600' : 'text-green-600'}`}>{message}</p>}
        </DashboardCard>
    );
}


// --- 3. Main Exam Admin Dashboard Component ---
export default function ExamAdminDashboard() {
    const { userProfile } = useAuth();

    // Placeholder for dashboard stats
    const [stats] = useState({ totalUsers: 3, examsScheduled: 0, pendingApps: 0 });

    return (
        <div className="p-8 space-y-8 bg-gray-50 min-h-screen">
            <h1 className="text-3xl font-extrabold text-gray-900">
                Exam Admin Dashboard
                {userProfile?.role && <span className="text-xl text-purple-600 ml-3">({userProfile.role})</span>}
            </h1>

            {/* Admin Overview (Sample Cards) */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <DashboardCard title="Total Users" value={stats.totalUsers} color="blue" />
                <DashboardCard title="Exams Scheduled" value={stats.examsScheduled} color="yellow" />
                <DashboardCard title="Pending Applications" value={stats.pendingApps} color="red" />
                <DashboardCard title="Portal Status" value="Open" color="green" />
            </div>

            {/* Management Panels */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ExaminerManagement />
                <AssignmentControl />
            </div>

            {/* Links to detailed pages based on your structure */}
            <div className="space-y-4">
                <h2 className="text-xl font-bold">Quick Navigation</h2>
                <div className="flex flex-wrap gap-4">
                    {/* These Link components now work correctly */}
                    <Link to="/admin/events" className="text-blue-600 hover:text-blue-800 font-medium">Manage Events</Link>
                    <Link to="/admin/applications" className="text-blue-600 hover:text-blue-800 font-medium">Manage Staff Apps</Link>
                    <Link to="/admin/exams" className="text-blue-600 hover:text-blue-800 font-medium">Manage Exams</Link>

                    {/* --- NEW LINKS ADDED --- */}
                    <Link to="/admin/finance" className="text-red-600 hover:text-red-800 font-medium">Go to Finance Dashboard</Link>
                    <Link to="/admin/support" className="text-purple-600 hover:text-purple-800 font-medium">Go to Support Dashboard</Link>
                    <Link to="/staff/my-tickets" className="text-red-400 hover:text-red-600 font-medium">My Internal Requests</Link>
                </div>
            </div>
        </div>
    );
}
